import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, MapPin, Star, Building2, Download } from "lucide-react";
import { sr } from "../../lib/sr";

export default function PlacesImport() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [importing, setImporting] = useState(null);

  const search = async (e) => {
    e?.preventDefault?.();
    if (q.trim().length < 2) return;
    setSearching(true);
    try { const r = await sr.placesSearch(q.trim()); setResults(r.results || []); }
    catch (e2) { toast.error(e2?.response?.data?.detail || "Search failed"); setResults([]); }
    finally { setSearching(false); }
  };

  const doImport = async (p) => {
    setImporting(p.place_id);
    try {
      const r = await sr.placesImport(p.place_id);
      toast.success(`Imported ${r.name}`);
      nav(`/app/admin/clients/${r.tenant_id}`);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not import"); }
    finally { setImporting(null); }
  };

  return (
    <div data-testid="places-import-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><MapPin size={12} /> Client setup</span>
        <h1 style={{ fontSize: 30 }}>Import a client from Google</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Search a business and set them up in seconds — name, address, phone, website and category prefilled.</p>
      </div>

      <form onSubmit={search} className="sr-card" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 18 }}>
        <Search size={18} style={{ color: "var(--muted)", flexShrink: 0 }} />
        <input className="sr-input" style={{ flex: 1 }} placeholder="e.g. Sunrise Bistro, Dallas TX" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="places-search-input" />
        <button className="sr-btn sr-btn-primary sr-btn-sm" type="submit" disabled={searching || q.trim().length < 2} data-testid="places-search-btn">
          {searching ? <Loader2 size={14} className="sr-spin" /> : <>Search</>}
        </button>
      </form>

      {results !== null && (
        results.length === 0 ? (
          <div className="sr-card" data-testid="places-empty"><p className="sr-muted" style={{ fontSize: 13 }}>No businesses matched. Try adding a city.</p></div>
        ) : (
          <div style={{ display: "grid", gap: 10 }} data-testid="places-results">
            {results.map((p) => (
              <div key={p.place_id} className="sr-card" data-testid={`place-${p.place_id}`} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Building2 size={18} style={{ color: "var(--blue)" }} />
                </div>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ fontSize: 15, color: "var(--navy)", fontWeight: 700 }}>{p.name}</div>
                  <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span>{p.address}</span>
                    {p.category && <span className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>{p.category}</span>}
                    {p.rating != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><Star size={11} style={{ color: "#F2A33D" }} /> {p.rating}</span>}
                  </div>
                </div>
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => doImport(p)} disabled={importing === p.place_id} data-testid={`import-${p.place_id}`}>
                  {importing === p.place_id ? <Loader2 size={14} className="sr-spin" /> : <><Download size={14} /> Import</>}
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
